import type { ApproverGroupDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { Icon } from '../lib/icons';
import { UserSelect } from '../lib/tasks-ui';
import { Empty, Person, useToast } from '../lib/ui';

function GroupForm({ group, onDone }: { group: ApproverGroupDto | null; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(group?.members.map((m) => m.id) ?? []);
  const [adding, setAdding] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body = { name, description: description || null, memberIds };
      return group ? api.patch(`/approver-groups/${group.id}`, body) : api.post('/approver-groups', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approver-groups'] });
      toast(group ? 'Группа сохранена' : 'Группа создана');
      onDone();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/approver-groups/${group!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approver-groups'] });
      toast('Группа удалена');
      onDone();
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const known = new Map(group?.members.map((m) => [m.id, m]) ?? []);

  return (
    <form className="card card-pad" style={{ display: 'grid', gap: 14 }} onSubmit={submit}>
      <h2>{group ? group.name : 'Новая группа'}</h2>
      {err && !Object.keys(err.fields).length && <div className="alert">{err.message}</div>}
      <label className="field">
        <span>Название</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus placeholder="Например: Бухгалтерия, Закупщики" aria-invalid={Boolean(err?.fields.name)} />
        {err?.fields.name && <span className="field-error">{err.fields.name}</span>}
      </label>
      <label className="field">
        <span>Описание</span>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Кто входит и за что отвечает" />
      </label>
      <div className="field">
        <span>Состав</span>
        <MemberList ids={memberIds} known={known} onRemove={(id) => setMemberIds((l) => l.filter((x) => x !== id))} />
        <div className="step-edit-row">
          <UserSelect value={adding} onChange={setAdding} exclude={memberIds} placeholder="Добавить сотрудника" />
          <button
            type="button"
            className="btn"
            disabled={!adding}
            onClick={() => {
              setMemberIds((l) => [...l, adding]);
              setAdding('');
            }}
          >
            Добавить
          </button>
        </div>
      </div>
      {group && group.usedIn.length > 0 && <p className="faint">Используется в маршрутах: {group.usedIn.join(', ')}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={save.isPending || !name.trim()}>
          Сохранить
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Отмена
        </button>
        {group && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', color: 'var(--danger)' }}
            disabled={remove.isPending}
            onClick={() => confirm(`Удалить группу «${group.name}»?`) && remove.mutate()}
          >
            Удалить
          </button>
        )}
      </div>
    </form>
  );
}

function MemberList({ ids, known, onRemove }: { ids: string[]; known: Map<string, { fullName: string; position: string | null }>; onRemove: (id: string) => void }) {
  const users = useQuery({ queryKey: ['users', 'picker'], queryFn: () => api.get<{ items: Array<{ id: string; fullName: string; position: string | null }> }>('/users', { limit: 200 }), staleTime: 300_000 });
  if (ids.length === 0) return <span className="faint">Пока никого — добавьте сотрудников</span>;
  return (
    <div className="member-list">
      {ids.map((id) => {
        const u = users.data?.items.find((x) => x.id === id) ?? known.get(id);
        return (
          <span key={id} className="member">
            <Person name={u?.fullName ?? '…'} sub={u?.position} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onRemove(id)} aria-label={`Убрать ${u?.fullName ?? ''}`}>
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
}

/** Группы согласующих: в маршруте ставится группа, состав правится здесь. */
export function GroupsPage() {
  const groups = useQuery({ queryKey: ['approver-groups'], queryFn: () => api.get<ApproverGroupDto[]>('/approver-groups') });
  const [editing, setEditing] = useState<ApproverGroupDto | 'new' | null>(null);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Группы согласующих</h1>
          <p className="muted">
            В маршруте указывается группа — «любой один из группы» или «каждый». Уволился сотрудник — поправьте состав здесь, маршруты менять не нужно.
          </p>
        </div>
        {editing === null && (
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={18} /> Новая группа
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 14, maxWidth: 820 }}>
        {editing !== null && <GroupForm key={editing === 'new' ? 'new' : editing.id} group={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
        {groups.data?.length === 0 && editing === null && (
          <div className="card">
            <Empty emoji="👥" title="Групп пока нет">Например: «Бухгалтерия» — главный бухгалтер и заместитель, согласует любой из них</Empty>
          </div>
        )}
        {groups.data?.map((g) => (
          <button key={g.id} className="card card-pad route-card" style={{ textAlign: 'left' }} onClick={() => setEditing(g)}>
            <div className="section-head">
              <b>{g.name}</b>
              <span className="faint">{g.members.length} чел.</span>
            </div>
            {g.description && <p className="faint">{g.description}</p>}
            <div className="chain">
              {g.members.map((m) => (
                <span key={m.id} className="chip">
                  {m.fullName}
                </span>
              ))}
            </div>
            {g.usedIn.length > 0 && <span className="faint">В маршрутах: {g.usedIn.join(', ')}</span>}
          </button>
        ))}
      </div>
    </>
  );
}
